import React from 'react';
import { DotProps } from 'recharts';
import { token } from '@atlaskit/tokens';
import { css } from '@emotion/core';

import { DataPointWithEvents } from '@/ui';
import {
  BREACH_COLOR,
  HISTORICAL_ALERT_COLOR,
  BREACH_LIGHT_COLOR,
  BURN_LIGHT_COLOR,
  BURN_COLOR,
  colors,
  INVERSE_COLOR,
  HISTORICAL_LIGHT_ALERT_COLOR,
  CTD_ERROR_RATE_COLOR,
  LOW_TRAFFIC_ALERT_COLOR,
} from '@/styles';
import { getEventTypes } from '@/components/composed/slo-card/utils';

import DotCircle from './dot-circle';
import { DOT_RADIUS } from './constants';
import { GraphEventSource } from './graph-tabs';

type CustomDotProps = DotProps & {
  payload?: DataPointWithEvents;
  graphEventSource?: GraphEventSource;
};

const incidentIconStyles = css({
  fontSize: DOT_RADIUS * 2,
  textAnchor: 'middle',
});

const Dot: React.FC<CustomDotProps> = props => {
  /* eslint-disable react/destructuring-assignment */
  const cx = props.cx!;
  const cy = props.cy!;
  const payload = props.payload!;

  let offsetY = 0;

  const { graphEventSource } = props;
  const showHistoricalEvents = graphEventSource === GraphEventSource.Historical;

  if (payload.result && (payload.events.length || payload.incidents.length)) {
    const {
      breachEvents,
      burnEvents,
      sfxBreachEvents,
      sfxBurnEvents,
      lowTrafficBreachEvents,
      lowTrafficBurnEvents,
      lowTrafficSFxBreachEvents,
      lowTrafficSFxBurnEvents,
      sfxCtdErrorRateEvents,
      sfxLowTrafficAlertEvents,
    } = getEventTypes(payload.events, graphEventSource);
    const hasEvents =
      (payload.events || []).filter(event =>
        showHistoricalEvents ? event.isFromHistoricalIndicator : !event.isFromHistoricalIndicator
      ).length > 0;

    const breachCount = breachEvents.length + sfxBreachEvents.length;
    const lowTrafficBreachCount = lowTrafficBreachEvents.length + lowTrafficSFxBreachEvents.length;
    const burnCount = burnEvents.length + sfxBurnEvents.length;
    const lowTrafficBurnCount = lowTrafficBurnEvents.length + lowTrafficSFxBurnEvents.length;
    const ctdErrorRateCount = sfxCtdErrorRateEvents.length;
    const lowTrafficAlertCount = sfxLowTrafficAlertEvents.length;

    /* eslint-disable-next-line no-return-assign */
    return (
      <g>
        {hasEvents && (
          <circle
            cx={cx}
            cy={cy}
            r={4}
            fill={token('color.text', colors.N800)}
            stroke={token('elevation.surface', '#FFF')}
            strokeWidth={2}
          />
        )}
        {payload.incidents.length > 0 && (
          <g>
            <text x={cx} y={cy - 10} css={incidentIconStyles}>
              {String.fromCodePoint(0x1f525)}
            </text>
          </g>
        )}
        {breachCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR}
            stroke={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={INVERSE_COLOR}
            count={breachCount}
          />
        )}
        {lowTrafficBreachCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BREACH_LIGHT_COLOR}
            stroke={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR}
            count={lowTrafficBreachCount}
          />
        )}
        {burnCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR}
            stroke={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={INVERSE_COLOR}
            count={burnCount}
          />
        )}
        {lowTrafficBurnCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BURN_LIGHT_COLOR}
            stroke={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR}
            count={lowTrafficBurnCount}
          />
        )}
        {ctdErrorRateCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : CTD_ERROR_RATE_COLOR}
            stroke={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : CTD_ERROR_RATE_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={INVERSE_COLOR}
            count={ctdErrorRateCount}
          />
        )}
        {lowTrafficAlertCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : LOW_TRAFFIC_ALERT_COLOR}
            stroke={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : LOW_TRAFFIC_ALERT_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={INVERSE_COLOR}
            count={lowTrafficAlertCount}
          />
        )}
      </g>
    );
  }

  return null;
};

export default Dot;
